import { Fab, Button, Dialog, DialogTitle, DialogContent, DialogActions, MenuList, Menu, MenuItem, ListItemIcon, ListItemText, Divider, Typography } from "@mui/material"
import { DataGrid, GridColDef, useGridApiRef } from "@mui/x-data-grid"
import { useEffect, useState } from "react"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import AddIcon from '@mui/icons-material/Add'
import { Localized, useLocalization } from "@fluent/react"
import { unwrapResult } from "@reduxjs/toolkit"
import { LoadingContainer } from "../../app/LoadingContainer"
import { ErrorContainer } from "../../app/ErrorContainer"
import { useNavigate } from "react-router-dom"
import { Action, AIAccess, AIStatistics, createAIAccess, loadAIAccess, loadAIAccessForCompany, loadAIStaticticsForCompany } from "./aiQuestionsSlice"
import { CompaniesBrowser } from "../company/CompaniesBrowser"
import { If } from "../../app/If"
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import TimelapseIcon from '@mui/icons-material/Timelapse';
import { ActionRunner } from "./ActionRunner"
import produce from "immer";
import { enUS, plPL } from "@mui/x-data-grid/locales"
import { selectSettings } from "../settings/settingsSlice"
import { QuickSearchToolbar } from "../../app/QuickSearchToolbar"

interface AIAccessForCompanyProps {
    companyId: string | undefined;
    companyName: string | undefined;
    onSave?: () => void;
}

export const AIAccessForCompany = (props: AIAccessForCompanyProps) => {
    const dispatch = useAppDispatch()
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [aiAccess, setAIAccess] = useState<AIAccess | null>(null);
    const [aiStatistics, setAIStatistics] = useState<AIStatistics>();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const openMenu = Boolean(anchorEl);
    const [actionToRun, setActionToRun] = useState<Action & { name: string } | null>(null)

    useEffect(() => {
        if(props.companyId !== undefined) {
            setIsLoading(true);
            dispatch(loadAIAccessForCompany(props.companyId))
            .then(unwrapResult)
            .then(setAIAccess)
            .catch((error) => console.log(error))
            dispatch(loadAIStaticticsForCompany(props.companyId))
            .then(unwrapResult)
            .then(setAIStatistics)
            .catch((error) => console.log(error))
            .finally(() => setIsLoading(false));
        }
    }, [])

    const getStatus = () => {
        switch (aiAccess?.status) {
            case "ACTIVE":
                return <Localized id="yes">Tak</Localized>
            case "HOLD":
                return <Localized id="ai-onhold">Wstrzymany</Localized>
            default:
                return <Localized id="no">Nie</Localized>
        }
    }

    const setAccess = () => {
        if(props.companyId !== undefined) {
            dispatch(createAIAccess({ companyId: Number(props.companyId) })).then(unwrapResult).then(result => {
                setAIAccess(result);
                if(props.onSave) props.onSave();
            })
        }
    }

    if (isLoading){
        return <>
            <LoadingContainer/>
        </>
    }

    return <>
        <If condition={aiAccess !== null} otherwise={
            <Button onClick={setAccess} color="secondary" variant="contained">
                <Localized id="ai-grant-access">Nadaj dostęp do AI</Localized>
            </Button>
        }>
            <Typography><Localized id="ai-settings-access">Dostęp do AI</Localized>: {getStatus()}</Typography>
            <Typography>Limit: {aiAccess?.limit ?? "-"}</Typography>
            <Typography><Localized id="ai-settings-total-tokens">Użyte tokeny</Localized>: {aiStatistics?.totalTokens ?? 0}</Typography>
            <Button onClick={(event: React.MouseEvent<HTMLButtonElement>) => { setAnchorEl(event.currentTarget); }} color="secondary" variant="outlined">
                <Localized id="settings">Ustawienia</Localized>
            </Button>
            <Menu anchorEl={anchorEl} open={openMenu} onClose={() => { setAnchorEl(null); } }>
                <MenuList>
                    <If condition={aiAccess?.status !== 'ACTIVE'}>
                        <MenuItem onClick={() => { setActionToRun({ id: aiAccess?.id as string, actionId: "activeAccess", value: "", type: "PUT", name: props.companyName as string }); setAnchorEl(null); } }>
                            <ListItemIcon>
                                <PlayArrowIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>
                                <Localized id="ai-activate">Aktywuj</Localized>
                            </ListItemText>
                        </MenuItem>
                    </If>
                    <If condition={aiAccess?.status !== 'HOLD'}>
                        <MenuItem onClick={() => { setActionToRun({ id: aiAccess?.id as string, actionId: "holdAccess", value: "", type: "PUT", name: props.companyName as string }); setAnchorEl(null); } }>
                            <ListItemIcon>
                                <PauseIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>
                                <Localized id="ai-hold">Wstrzymaj</Localized>
                            </ListItemText>
                        </MenuItem>
                    </If>
                    <MenuItem onClick={() => { setActionToRun({ id: aiAccess?.id as string, actionId: "changeLimitAccess", value: String(aiAccess?.limit), type: "PUT", name: props.companyName as string }); setAnchorEl(null); } }>
                        <ListItemIcon>
                            <TimelapseIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>
                            <Localized id="ai-change-limit">Zmień limit</Localized>
                        </ListItemText>
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={() => { setActionToRun({ id: aiAccess?.id as string, actionId: "deleteAccess", value: "", type: "DELETE", name: props.companyName as string }); setAnchorEl(null); } }>
                        <ListItemIcon>
                            <DeleteIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>
                            <Localized id="delete">Delete</Localized>
                        </ListItemText>
                    </MenuItem>
                </MenuList>
            </Menu>
            {actionToRun && <ActionRunner
                action={actionToRun}
                onError={() => { }}
                onSuccess={result => {
                    setActionToRun(null)
                    if(result.actionId === "activeAccess" || result.actionId === "holdAccess" || result.actionId === "changeLimitAccess") {
                        setAIAccess(produce(draft => {
                            if(draft) {
                                draft.status = result.status;
                                draft.limit = result.limit;
                            }
                        }))
                    } else if(result.actionId === "deleteAccess") {
                        setAIAccess(null)
                    }
                }}
                onCancel={() => setActionToRun(null)} />}
        </If>
    </>
}

export const AIAccessGrid = () => {
    const { locale } = useAppSelector(selectSettings);
    const { l10n } = useLocalization()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const [aiAccess, setAIAccess] = useState<AIAccess[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isError, setIsError] = useState<boolean>(false)
    const [error, setError] = useState(null);
    const [companiesDialogOpen, setCompaniesDialogOpen] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [openMenu, setOpenMenu] = useState(null);
    const [actionToRun, setActionToRun] = useState<Action & { name: string } | null>(null)

    const companiesGridRef = useGridApiRef();

    useEffect(() => {
        setIsLoading(true);
        dispatch(loadAIAccess())
        .then(unwrapResult)
        .then(setAIAccess)
        .catch((error) => { console.log(error); setIsError(true); setError(error); })
        .finally(() => setIsLoading(false));
    }, [])
    
    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };

    const onConfirmClick = () => {
        companiesGridRef.current.getSelectedRows().forEach((f) => {
            dispatch(createAIAccess({ companyId: f.id })).then(unwrapResult).then(result => {
                setAIAccess(produce(draft => {
                    if(draft) {
                        draft.push(result);
                    }
                }))
            });
        })
        setCompaniesDialogOpen(false);
    }

    const columns: GridColDef[] = [
        {
            field: 'companyName',
            headerName: l10n.getString("companies-name", null, "Nazwa firmy"),
            flex: 1
        },
        {
            field: 'limit',
            headerName: 'Limit'
        },
        {
            field: 'status', 
            headerName: 'Status'
        },
        {
            field: 'actions',
            type: 'actions',
            width: 200,
            renderCell: (params) => {
                return (
                    <>
                        <Button onClick={(event: React.MouseEvent<HTMLButtonElement>) => { setAnchorEl(event.currentTarget); setOpenMenu(params.row.id) }} color="secondary" variant="outlined">
                            <Localized id="actions">Akcje</Localized>
                        </Button>
                        <Menu anchorEl={anchorEl} open={openMenu === params.row.id} onClose={() => { setAnchorEl(null); setOpenMenu(null); } }>
                        <MenuList>
                            <If condition={params.row.status !== 'ACTIVE'}>
                                <MenuItem onClick={() => { setActionToRun({ id: params.row.id, actionId: "activeAccess", value: "", type: "PUT", name: params.row.companyName }); setAnchorEl(null); setOpenMenu(null); } }>
                                    <ListItemIcon>
                                        <PlayArrowIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText>
                                        <Localized id="ai-activate">Aktywuj</Localized>
                                    </ListItemText>
                                </MenuItem>
                            </If>
                            <If condition={params.row.status !== 'HOLD'}>
                                <MenuItem onClick={() => { setActionToRun({ id: params.row.id, actionId: "holdAccess", value: "", type: "PUT", name: params.row.companyName }); setAnchorEl(null); setOpenMenu(null); } }>
                                    <ListItemIcon>
                                        <PauseIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText>
                                        <Localized id="ai-hold">Wstrzymaj</Localized>
                                    </ListItemText>
                                </MenuItem>
                            </If>
                            <MenuItem onClick={() => { setActionToRun({ id: params.row.id, actionId: "changeLimitAccess", value: String(params.row.limit), type: "PUT", name: params.row.companyName }); setAnchorEl(null); setOpenMenu(null); } }>
                                <ListItemIcon>
                                    <TimelapseIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>
                                    <Localized id="ai-change-limit">Zmień limit</Localized>
                                </ListItemText>
                            </MenuItem>
                            <Divider />
                            <MenuItem onClick={() => { setActionToRun({ id: params.row.id, actionId: "deleteAccess", value: "", type: "DELETE", name: params.row.companyName }); setAnchorEl(null); setOpenMenu(null); } }>
                                <ListItemIcon>
                                    <DeleteIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>
                                    <Localized id="delete">Delete</Localized>
                                </ListItemText>
                            </MenuItem>
                        </MenuList>
                    </Menu>
                </>
                )
            }
        }
    ]

    if (isError){
        return <>
            <ErrorContainer error={error}/>
        </>
    }

    if (isLoading){
        return <>
            <LoadingContainer/>
        </>
    }
    
    return (
        <>
            <DataGrid
                style={{ minHeight: 100, height: 'calc(100vh - 300px)' }}
                localeText={getCustomLocaleText()}
                columns={columns}
                rows={aiAccess}
                initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
                slots={{ toolbar: QuickSearchToolbar }}
            />
            <Fab sx={{ mt: 2 }} color="secondary" onClick={() => setCompaniesDialogOpen(true)}>
                <AddIcon />
            </Fab>
            <Dialog open={companiesDialogOpen} onClose={() => setCompaniesDialogOpen(false) } fullWidth maxWidth='lg'>
                <DialogTitle>
                    <Localized id="companies">Firmy</Localized>
                </DialogTitle>
                <DialogContent>
                    <CompaniesBrowser gridRef={companiesGridRef}/>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onConfirmClick}>
                        <Localized id='ok'>OK</Localized>
                    </Button>
                    <Button onClick={() => setCompaniesDialogOpen(false)}>
                        <Localized id='cancel'>Anuluj</Localized>
                    </Button>
                </DialogActions>
            </Dialog>
            <>{actionToRun && <ActionRunner
                action={actionToRun}
                onError={() => { }}
                onSuccess={result => {
                    setActionToRun(null)
                    if(result.actionId === "activeAccess" || result.actionId === "holdAccess" || result.actionId === "changeLimitAccess") {
                        setAIAccess(produce(draft => {
                            if(draft) {
                                const element = draft.find(f => f.id === result.id);
                                if(element) {
                                    element.status = result.status;
                                    element.limit = result.limit;
                                }
                            }
                        }))
                    } else if(result.actionId === "deleteAccess") {
                        setAIAccess(produce(draft => {
                            if(draft) {
                                const element = draft.findIndex(f => f.id === result.id);
                                if(element !== -1) {
                                    draft.splice(element, 1);
                                }
                            }
                        }))
                    }
                }}
                onCancel={() => setActionToRun(null)} />}</>
        </>
    )
}
